import { BarDropdown } from '../bar-dropdown/BarDropdown'
import React, { useRef, useState } from 'react'
import { Avatar, Button, Card, Tooltip } from 'antd'
import styles from './styles.module.scss'
import buttonStyles from '../../common/button/styles.module.scss'
import { UserOutlined } from '@ant-design/icons'
import { Routes } from '../../routing/routes'
import { ChevronDownIcon } from '../../icons/ChevronDownIcon'

const logoutApi = (): Promise<void> => {
  return fetch(process.env.REACT_APP_REST_PREFIX + '/logout', {
    method: 'POST',
    headers: {
      'Accept': 'application/json',
      'Content-Type': 'application/json',
    },
    ...(process.env.NODE_ENV === 'development' && { credentials: 'include' })
  })
    .then(() => {
      window.location.href = Routes.Login;
    })
    .catch(error => {
      console.error(error);
    })
}

export type User = {
  name: string
  firstName?: string
  lastName?: string
  email?: string
  role: string
  jobTitle?: string
  company: string
  accountId?: string
}

export type UserPanelProps = {
  user: User | undefined
}

const formatRole = (role: string) => {
  return role.toUpperCase().replaceAll('_', ' ').trim()
}

const formatDescription = (
  jobTitle: string | undefined,
  company: string
): string => {
  if (jobTitle === null || jobTitle === undefined) {
    return company
  }

  return `${jobTitle} at ${company}`
}

const propagateClick = (
  isUserPanelOpen: boolean,
  buttonRef: React.MutableRefObject<HTMLButtonElement | null>
) => {
  if (buttonRef.current !== null) {
    buttonRef.current.click()
    if (isUserPanelOpen) {
      buttonRef.current.blur()
    } else {
      buttonRef.current.focus()
    }
  }
}

const propagateHover = (
  hover: boolean,
  isUserPanelOpen: boolean,
  buttonRef: React.MutableRefObject<HTMLButtonElement | null>
) => {
  if (buttonRef.current !== null) {
    if (isUserPanelOpen) {
      return
    }
    if (hover) {
      buttonRef.current.focus()
    } else {
      buttonRef.current.blur()
    }
  }
}

const Icon = ({ isUserPanelOpen }: { isUserPanelOpen: boolean }) => {
  const buttonRef = useRef(null)

  return (
    <div
      onMouseEnter={() => {
        propagateHover(true, isUserPanelOpen, buttonRef)
      }}
      onMouseLeave={() => {
        propagateHover(false, isUserPanelOpen, buttonRef)
      }}
      onClick={() => {
        propagateClick(isUserPanelOpen, buttonRef)
      }}
    >
      <Tooltip
        overlayClassName='nav-bar-app'
        placement='bottom'
        title='User Profile'
      >
        <Button
          ref={buttonRef}
          className={styles.userProfileBtn}
          shape='circle'
          icon={<UserOutlined />}
        />
      </Tooltip>
      {isUserPanelOpen ? (
        <ChevronDownIcon className={styles.chevronUpIcon} />
      ) : (
        <ChevronDownIcon />
      )}
    </div>
  )
}

const Panel = ({ user }: { user: User }) => {

  return (
    <Card className={styles.userDetailsCard}>
      <Card.Grid hoverable={false} className={styles.cardGridItem}>
        <Card.Meta
          className={styles.userPanelMeta}
          avatar={<Avatar size={70} icon={<UserOutlined />} />}
          title={
            <div className={styles.userPanelDetails}>
              <span className={styles.roleText}>{formatRole(user.role)}</span>
              <br />
              <span className={styles.userNameText}>{user.name}</span>
              <br />
              <span className={styles.descriptionText}>
                {formatDescription(user.jobTitle, user.company)}
              </span>
            </div>
          }
        />
      </Card.Grid>
      <Card.Grid hoverable={false} className={styles.cardGridItem}>
        <a key='my-account' href={Routes.MyAccount}>
          <Button className={buttonStyles.btnSecondary}>My Account</Button>
        </a>
        {['SUPER_ADMIN', 'ADMIN'].includes(user.role) ? (
          <a key='control-panel' href={Routes.Admin}>
            <Button className={buttonStyles.btnSecondary}>Control Panel</Button>
          </a>
        ) : null}
        {user.role === 'SUPER_ADMIN' ? (
          <a key='teams' href={Routes.Teams}>
            <Button className={buttonStyles.btnSecondary}>Teams</Button>
          </a>
        ) : null}
        <Button onClick={logoutApi} className={buttonStyles.btnSecondary}>Sign out</Button>
      </Card.Grid>
    </Card>
  )
}

export const UserDetailsPanel = ({ user }: UserPanelProps) => {
  const [isOpen, setIsOpen] = useState(false)
  return (
    <BarDropdown
      onVisibleChange={setIsOpen}
      isContentAvailable={user !== undefined}
      icon={<Icon isUserPanelOpen={isOpen} />}
      dataTestId={'user-details-panel-dropdown'}
    >
      <Panel user={user as User} />
    </BarDropdown>
  )
}
